<template>
  <div class="device-general">
    <header class="device-header">
      <div>
        <p :class="['device-serial', { mono: device.serial_number }]">
          {{ device.serial_number || "Unclaimed device" }}
        </p>
        <p class="device-meta">{{ headerMeta }}</p>
      </div>
      <div class="tags">
        <span :class="['tag', statusClass(device.camera_status)]">{{ device.camera_status }}</span>
        <span v-if="device.revoked_at" class="tag is-danger">revoked</span>
      </div>
    </header>

    <div v-if="claimResult" class="setup-label">
      <figure v-if="qrCode">
        <img :src="qrCode" alt="Setup QR code" width="112" height="112" />
      </figure>
      <div>
        <p class="section-title">Setup label</p>
        <p class="setup-code">{{ claimResult.label }}</p>
        <p class="hint">Shown only now. Copy or print it before closing this window.</p>
        <div class="buttons are-small mt-2">
          <button class="button" @click="copyLabel">Copy</button>
          <button class="button" @click="printLabel">Print</button>
        </div>
      </div>
    </div>

    <section v-if="hasIdentity" class="device-section">
      <p class="section-title">Connection</p>
      <dl class="facts">
        <dt>Power</dt>
        <dd><span :class="['tag', power.className]">{{ power.label }}</span></dd>
        <template v-if="device.run_status">
          <dt>Capture</dt>
          <dd>{{ captureText }}</dd>
        </template>
        <dt>Last report</dt>
        <dd>{{ formatDate(device.last_seen_at) || "Never" }}</dd>
        <template v-if="!device.revoked_at">
          <dt>Keep awake</dt>
          <dd class="keep-awake">
            <button
              type="button"
              role="switch"
              :aria-checked="String(keepAwakeOn)"
              aria-label="Keep awake on next wake"
              :class="['switch', { on: keepAwakeOn }]"
              :disabled="isWorking || device.remote_delivered"
              @click="toggleKeepAwake"
            >
              <span class="knob"></span>
            </button>
            <span :class="{ 'has-text-grey': !keepAwakeOn }">{{ keepAwakeText }}</span>
          </dd>
        </template>
      </dl>
    </section>

    <section class="device-section">
      <p class="section-title">Site</p>
      <dl class="facts">
        <template v-for="row in siteRows">
          <dt :key="`${row.label}-label`">{{ row.label }}</dt>
          <dd :key="`${row.label}-value`">{{ row.value }}</dd>
        </template>
      </dl>
      <p v-if="device.camera_status === 'returning'" :class="['return-state', { ready: returnState.ready }]">
        {{ returnMessage }}
      </p>
      <div v-if="primaryActions.length" class="buttons mt-3">
        <button
          v-for="action in primaryActions"
          :key="action.key"
          :class="['button', 'is-small', action.className, { 'is-loading': isWorking }]"
          :disabled="action.disabled"
          @click="start(action)"
        >
          {{ action.label }}
        </button>
      </div>
      <div v-if="picker === 'assign'" class="field mt-3" data-inline-panel>
        <searchable-select
          :endpoint="camerasEndpoint"
          :result-filter="isAssignableCamera"
          placeholder="Search Pro Camera sites..."
          @select="assign"
        />
      </div>
      <action-confirm
        v-if="confirming && confirming.area === 'site'"
        data-inline-panel
        :message="confirming.confirm"
        :label="confirming.label"
        :loading="isWorking"
        @confirm="run(confirming)"
        @cancel="confirming = null"
      />
    </section>

    <section v-if="hasIdentity && device.serial_number" class="device-section">
      <p class="section-title">Ownership</p>
      <dl class="facts">
        <dt>Owner</dt>
        <dd>{{ ownerText }}</dd>
      </dl>
      <div v-if="ownershipActions.length" class="buttons mt-3">
        <button
          v-for="action in ownershipActions"
          :key="action.key"
          :class="['button', 'is-small', action.className, { 'is-loading': isWorking }]"
          :disabled="action.disabled"
          @click="start(action)"
        >
          {{ action.label }}
        </button>
      </div>
      <p v-if="device.camera_status === 'returning'" class="hint">
        Ownership and allocation cannot change while the device is waiting for return inspection.
      </p>

      <div v-if="picker === 'owner'" class="inline-form" data-inline-panel>
        <div class="field">
          <label class="label is-small">Owner</label>
          <div class="choice">
            <label class="radio"><input v-model="ownerMode" type="radio" value="company" /> A company (sale)</label>
            <label class="radio"><input v-model="ownerMode" type="radio" value="natilon" /> Natilon (rental)</label>
          </div>
        </div>
        <div v-if="ownerMode === 'company'" class="field">
          <label class="label is-small">Company</label>
          <searchable-select
            :endpoint="companiesEndpoint"
            placeholder="Search companies..."
            @select="ownerCompany = $event"
          />
        </div>
        <p class="hint mb-3">
          This corrects the current ownership record and overwrites it.
          <template v-if="ownerCompanyId && !device.camera_id">For a real return or buyback use "Record return or buyback".</template>
        </p>
        <button
          class="button is-small is-primary"
          :disabled="ownerMode === 'company' && !ownerCompany"
          @click="confirmOwner"
        >
          Continue
        </button>
      </div>

      <div v-if="picker === 'takeBack'" class="inline-form" data-inline-panel>
        <div class="field">
          <label class="label is-small">Reason</label>
          <div class="choice">
            <label class="radio"><input v-model="takeBackReason" type="radio" value="return" /> Return</label>
            <label class="radio"><input v-model="takeBackReason" type="radio" value="buyback" /> Buyback</label>
          </div>
        </div>
        <div class="field">
          <label class="label is-small" for="take-back-note">Note</label>
          <input id="take-back-note" v-model="takeBackNote" class="input is-small" type="text" maxlength="2000" />
        </div>
        <button class="button is-small is-primary" @click="confirmTakeBack">Continue</button>
      </div>

      <div v-if="picker === 'allocate'" class="inline-form" data-inline-panel>
        <div class="field">
          <label class="label is-small">Company</label>
          <searchable-select
            :endpoint="companiesEndpoint"
            placeholder="Search companies..."
            @select="allocationCompany = $event"
          />
        </div>
        <p class="hint mb-3">Only this company will be able to install the device.</p>
        <button class="button is-small is-primary" :disabled="!allocationCompany" @click="confirmAllocation">
          Continue
        </button>
      </div>

      <action-confirm
        v-if="confirming && confirming.area === 'ownership'"
        data-inline-panel
        :message="confirming.confirm"
        :label="confirming.label"
        :loading="isWorking"
        @confirm="run(confirming)"
        @cancel="confirming = null"
      />
    </section>

    <section class="device-section">
      <p class="section-title">Device</p>
      <dl class="facts">
        <template v-for="row in deviceRows">
          <dt :key="`${row.label}-label`">{{ row.label }}</dt>
          <dd :key="`${row.label}-value`" :class="{ mono: row.mono }">{{ row.value }}</dd>
        </template>
      </dl>
      <div v-if="!hasIdentity" class="field has-addons mt-3">
        <div class="control">
          <div class="select is-small">
            <select v-model="legacyStatus" aria-label="Legacy status">
              <option value="created">created</option>
              <option value="pending">pending</option>
              <option value="claimed">claimed</option>
              <option value="active">active</option>
              <option value="disabled">disabled</option>
            </select>
          </div>
        </div>
        <div class="control">
          <button class="button is-small is-success" @click="saveLegacyStatus">Save status</button>
        </div>
      </div>
    </section>

    <section v-if="riskyActions.length" class="device-section risky">
      <p class="section-title">Maintenance</p>
      <div v-for="action in riskyActions" :key="action.key" class="risky-item">
        <div class="risky-row">
          <div>
            <p class="risky-label">{{ action.label }}</p>
            <p class="hint">{{ action.description }}</p>
          </div>
          <button
            :class="['button', 'is-small', action.className, { 'is-loading': isWorking }]"
            @click="start(action)"
          >
            {{ action.button }}
          </button>
        </div>
        <div v-if="action.key === 'rebind' && picker === 'rebind'" class="field mt-2" data-inline-panel>
          <searchable-select
            :endpoint="inventoriesEndpoint"
            :extra-params="unclaimedParams"
            label-key="device_id"
            placeholder="Search new devices by device ID..."
            @select="rebind"
          />
        </div>
        <action-confirm
          v-if="confirming && confirming.area === 'maintenance' && confirming.key === action.key"
          data-inline-panel
          :message="confirming.confirm"
          :label="confirming.label"
          :loading="isWorking"
          @confirm="run(confirming)"
          @cancel="confirming = null"
        />
      </div>
    </section>
  </div>
</template>

<script>
import QRCode from "qrcode";
import { httpInstance as http } from "@/plugins/http";
import endpoints from "@/api/endpoints";
import * as inventoryApi from "@/api/components";
import SearchableSelect from "@/components/SearchableSelect.vue";
import ActionConfirm from "./ActionConfirm.vue";
import { formatDate } from "@/utils/date_converter";
import { devicePower, returnState, runReasonText, statusClass } from "@/utils/pro_camera";
import toast from "@/utils/toast";

// The backend stores a missing IP as the literal "<nil>".
const present = (value) => value !== null && value !== undefined && value !== "" && value !== "<nil>";

export default {
  name: "ProCameraGeneralTab",
  components: { SearchableSelect, ActionConfirm },
  props: {
    deviceData: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      claimResult: null,
      qrCode: "",
      confirming: null,
      isWorking: false,
      picker: "",
      legacyStatus: this.deviceData.camera_status || "created",
      camerasEndpoint: endpoints.CAMERAS,
      inventoriesEndpoint: endpoints.PRO_CAMERA_INVENTORIES,
      unclaimedParams: { status: "unclaimed" },
      companiesEndpoint: endpoints.COMPANIES,
      allocationCompany: null,
      ownerMode: "company",
      ownerCompany: null,
      takeBackReason: "return",
      takeBackNote: "",
    };
  },
  computed: {
    device() {
      return this.deviceData;
    },
    hasIdentity() {
      return Boolean(this.device.key_fingerprint);
    },
    power() {
      return devicePower(this.device);
    },
    returnState() {
      return returnState(this.device);
    },
    headerMeta() {
      return [this.device.hardware_type, this.device.software_version && `v${this.device.software_version}`]
        .filter(present)
        .join(" · ");
    },
    returnMessage() {
      const { ready, wiped, wipedAt, pendingFiles } = this.returnState;
      if (ready) return `Wiped ${formatDate(wipedAt)}. Ready for stock after inspection.`;
      if (wiped) return `Wiped, but the last report does not confirm zero pending files (${pendingFiles ?? "unknown"}).`;
      return "Waiting for the device to upload pending files and report its wipe on the next wake.";
    },
    keepAwakeOn() {
      return Boolean(this.device.remote_request || this.device.remote_delivered);
    },
    keepAwakeText() {
      const device = this.device;
      if (device.remote_delivered) {
        const until = device.stay_until ? ` until ${formatDate(device.stay_until)}` : "";
        return `On · the device is awake for remote access${until}. It turns off after its next sleep report.`;
      }
      if (device.remote_request) {
        const when = device.sleeping_since && device.next_wake_at ? ` (${formatDate(device.next_wake_at)})` : "";
        return `On · the device will stay awake for 15 minutes when it next wakes${when}.`;
      }
      return "Off";
    },
    ownerCompanyId() {
      return this.device.owner_company_id || null;
    },
    // Same rule as the device's config: why it is or is not taking photos.
    captureText() {
      const { run_status: status, run_reason: reason } = this.device;
      if (status === "active") return "Capturing";
      return reason ? `Not capturing · ${runReasonText(reason)}` : "Not capturing";
    },
    // An installed device's code is already used; its open assignment says how it was installed.
    currentAssignment() {
      if (!this.device.camera_id) return null;
      return (this.device.ownership || []).find((row) => row.camera_id && !row.ended_at) || { kind: "rental" };
    },
    // One line: who owns the device, who uses it and who it is reserved for.
    ownerText() {
      const device = this.device;
      const parts = [];
      if (this.ownerCompanyId) {
        parts.push(`${device.owner_company_name || `Company #${this.ownerCompanyId}`} · purchased`);
      }
      else if (this.currentAssignment) {
        parts.push(`Natilon · rented to ${device.company_name || `the customer of camera #${device.camera_id}`}`);
      } else parts.push("Natilon");
      if (device.allocated_company_id && !device.camera_id && device.allocated_company_id !== this.ownerCompanyId) {
        parts.push(`reserved for ${device.allocated_company_name || `Company #${device.allocated_company_id}`}`);
      }
      return parts.join(" · ");
    },
    ownershipActions() {
      const device = this.device;
      if (!this.usable || device.camera_status === "returning") return [];
      const list = [{ key: "owner", label: "Change owner", className: "is-light", picker: true }];
      if (device.camera_id) return list;
      if (this.ownerCompanyId) {
        list.push({ key: "takeBack", label: "Record return or buyback", className: "is-light", picker: true });
      }
      list.push({
        key: "allocate",
        label: device.allocated_company_id ? "Change allocation" : "Allocate to company",
        className: "is-light",
        picker: true,
      });
      if (device.allocated_company_id) {
        list.push({
          key: "clearAllocation",
          label: "Clear allocation",
          className: "is-light",
          area: "ownership",
          confirm: this.ownerCompanyId
            ? "The allocation is removed. The owner can still install the device."
            : "Any company with a valid setup label will be able to install this device.",
        });
      }
      return list;
    },
    siteRows() {
      const device = this.device;
      const rows = [
        {
          label: "Camera",
          value: device.camera_id ? device.camera_name || `#${device.camera_id}` : "Not assigned",
        },
        { label: "Project", value: device.project_name },
        { label: "Company", value: device.company_name },
        { label: "Setup code used", value: formatDate(device.setup_code_used_at) },
      ];
      return rows.filter((row) => present(row.value));
    },
    deviceRows() {
      const device = this.device;
      return [
        { label: "Inventory ID", value: device.id },
        { label: "Device ID", value: device.device_id, mono: true },
        { label: "First seen IP", value: device.first_seen_ip, mono: true },
        { label: "Claimed", value: formatDate(device.claimed_at) },
        { label: "Revoked", value: formatDate(device.revoked_at) },
      ].filter((row) => present(row.value));
    },
    usable() {
      return this.hasIdentity && !this.device.revoked_at;
    },
    primaryActions() {
      const device = this.device;
      const status = device.camera_status;
      const list = [];
      if (this.usable && !device.camera_id && ["unclaimed", "pending"].includes(status)) {
        list.push({ key: "claim", label: "Claim and print label", className: "is-primary", area: "site" });
      }
      if (this.usable && !device.camera_id && status === "stock") {
        list.push({ key: "assign", label: "Assign to camera", className: "is-primary", picker: true, area: "site" });
        list.push({
          key: "claim",
          label: "Renew label",
          className: "is-light",
          area: "site",
          confirm: "The current setup code stops working. Replace the label on the device.",
        });
      }
      if (status === "returning") {
        list.push({
          key: "stock",
          label: "Approve stock",
          className: "is-success",
          area: "site",
          disabled: !this.returnState.ready,
          confirm: "Confirm the device was inspected and can return to stock.",
        });
      }
      return list;
    },
    riskyActions() {
      const device = this.device;
      const list = [];
      if (device.camera_status === "assigned" && device.camera_id) {
        list.push({
          key: "remove",
          area: "maintenance",
          label: "Remove from site",
          button: "Remove",
          className: "is-danger is-outlined",
          description: "Ends the assignment and archives the camera. Photos stay on the archived camera.",
          confirm: "The assignment ends and the camera is archived. Photos stay on the archived camera.",
        });
      }
      if (device.serial_number && device.camera_status !== "unclaimed") {
        list.push({
          key: "rebind",
          area: "maintenance",
          label: "Rebind new key",
          button: "Rebind",
          className: "is-light",
          picker: true,
          description: "Moves a newly registered key (for example after re-imaging) to this serial number.",
        });
      }
      if (this.usable) {
        list.push({
          key: "revoke",
          area: "maintenance",
          label: "Revoke",
          button: "Revoke",
          className: "is-danger is-outlined",
          description: "Blocks the device immediately. Only a new key and Rebind can bring it back.",
          confirm:
            "The device is blocked immediately. To restore it, the device must generate a new key and you must rebind it here.",
        });
      }
      return list;
    },
  },
  watch: {
    "deviceData.camera_status"(status) {
      this.legacyStatus = status;
    },
    // A picker or confirmation opened near the bottom can sit below the scrolled modal body.
    picker: "revealInlinePanel",
    confirming: "revealInlinePanel",
  },
  methods: {
    formatDate,
    statusClass,
    isAssignableCamera(camera) {
      if (camera.version !== 1 || camera.archived_at) return false;
      // Allocated or sold devices only go to that company's cameras; skip the check when the list lacks the company.
      const company = camera.project?.company_id ?? camera.project?.company?.id;
      const required = this.ownerCompanyId || this.device.allocated_company_id;
      return !required || company === undefined || company === required;
    },
    start(action) {
      if (action.picker) this.picker = this.picker === action.key ? "" : action.key;
      else if (action.confirm) this.confirming = action;
      else this.run(action);
    },
    revealInlinePanel() {
      this.$nextTick(() =>
        this.$el.querySelector("[data-inline-panel]")?.scrollIntoView({ block: "nearest", behavior: "smooth" })
      );
    },
    toggleKeepAwake() {
      this.run(
        this.keepAwakeOn
          ? { key: "remoteOff", label: "Keep awake off" }
          : { key: "remoteOn", label: "Keep awake on" }
      );
    },
    async run(action) {
      const id = this.device.id;
      const operations = {
        claim: async () => this.showLabel(await inventoryApi.claimInventory(id)),
        remove: () => inventoryApi.removeInventory(id),
        stock: () => inventoryApi.stockInventory(id),
        revoke: () => inventoryApi.revokeInventory(id),
        remoteOn: () => inventoryApi.remoteInventory(id, true),
        remoteOff: () => inventoryApi.remoteInventory(id, false),
        assign: () => inventoryApi.assignInventory(action.target.id, id),
        allocate: () => inventoryApi.allocateInventory(id, action.target.id),
        clearAllocation: () => inventoryApi.allocateInventory(id, null),
        owner: () => inventoryApi.setInventoryOwner(id, action.target ? action.target.id : null),
        takeBack: () => inventoryApi.takeBackInventory(id, action.reason, action.note),
        rebind: () => inventoryApi.rebindInventory(id, action.target.id),
      };
      this.isWorking = true;
      try {
        await operations[action.key]();
        toast.success(action.key.startsWith("remote") ? action.label : `${action.label} done`);
        this.picker = "";
        this.allocationCompany = null;
        this.ownerCompany = null;
        this.takeBackNote = "";
        this.$emit("changed");
      } catch (error) {
        toast.error(error.message);
      } finally {
        this.isWorking = false;
        this.confirming = null;
      }
    },
    assign(camera) {
      if (!camera) return;
      this.confirming = {
        key: "assign",
        label: "Assign",
        target: camera,
        area: "site",
        confirm: `Assign this device to ${camera.name}. Rental history starts now.`,
      };
    },
    confirmAllocation() {
      const company = this.allocationCompany;
      this.confirming = {
        key: "allocate",
        label: "Allocate",
        area: "ownership",
        target: company,
        confirm: `Only ${company.name} will be able to install this device.`,
      };
    },
    confirmTakeBack() {
      const reason = this.takeBackReason;
      const owner = this.device.owner_company_name || `Company #${this.ownerCompanyId}`;
      this.confirming = {
        key: "takeBack",
        label: reason === "return" ? "Record return" : "Record buyback",
        area: "ownership",
        reason,
        note: this.takeBackNote.trim(),
        confirm: `${owner} no longer owns this device. The sale stays in the history and the ${reason} is recorded.`,
      };
    },
    confirmOwner() {
      const company = this.ownerMode === "company" ? this.ownerCompany : null;
      this.confirming = {
        key: "owner",
        label: "Change owner",
        area: "ownership",
        target: company,
        confirm: company
          ? `${company.name} becomes the owner (sale). Only this company can install the device.`
          : "Natilon becomes the owner (rental). The current ownership record is overwritten; use this to correct a wrong record.",
      };
    },
    rebind(source) {
      if (!source) return;
      this.confirming = {
        key: "rebind",
        label: "Rebind",
        target: source,
        area: "maintenance",
        confirm: `Move key ${source.device_id} to this device. The old key stops working.`,
      };
    },
    async showLabel(result) {
      this.claimResult = result;
      this.qrCode = await QRCode.toDataURL(result.label, { margin: 1, width: 256 });
    },
    async copyLabel() {
      try {
        await navigator.clipboard.writeText(this.claimResult.label);
        toast.success("Label copied");
      } catch (error) {
        toast.error("Could not copy the label");
      }
    },
    printLabel() {
      const page = window.open("", "_blank", "width=420,height=420");
      if (!page) {
        toast.error("Allow pop-ups to print the label");
        return;
      }
      const image = page.document.createElement("img");
      const text = page.document.createElement("p");
      text.textContent = this.claimResult.label;
      text.style.fontFamily = "monospace";
      image.width = 192;
      image.onload = () => page.print();
      page.document.body.append(image, text);
      image.src = this.qrCode;
    },
    async saveLegacyStatus() {
      const response = await http.patch(endpoints.PRO_CAMERA_INVENTORIES, {
        id: this.device.id,
        status: this.legacyStatus,
      });
      if (response?.status > 299) {
        toast.error(response?.data?.message || "Failed to update inventory");
        return;
      }
      toast.success("Inventory updated");
      this.$emit("changed");
    },
  },
};
</script>

<style scoped>
.device-general { font-size: 0.875rem; }
.device-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #ededed;
}
.device-serial { font-size: 1.25rem; font-weight: 600; }
.device-meta { color: #7a7a7a; font-family: monospace; font-size: 0.75rem; }
.device-header .tags { margin-bottom: 0; flex-shrink: 0; }

.device-section { padding: 1rem 0; border-bottom: 1px solid #ededed; }
.device-section:last-child { border-bottom: 0; padding-bottom: 0; }
.section-title {
  margin-bottom: 0.5rem;
  color: #7a7a7a;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.facts { display: grid; grid-template-columns: 9rem 1fr; gap: 0.4rem 1rem; align-items: center; }
.facts dt { color: #7a7a7a; }
.facts dd { margin: 0; overflow-wrap: anywhere; }
.mono { font-family: monospace; }
.keep-awake { display: flex; align-items: center; gap: 0.75rem; }
.switch {
  position: relative;
  flex-shrink: 0;
  width: 2.5rem;
  height: 1.5rem;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: #d5d5d5;
  cursor: pointer;
  transition: background-color 0.2s ease;
}
.switch .knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: calc(1.5rem - 4px);
  height: calc(1.5rem - 4px);
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  transition: transform 0.2s ease;
}
.switch.on { background: #48c78e; }
.switch.on .knob { transform: translateX(1rem); }
.switch:disabled { cursor: not-allowed; opacity: 0.6; }
.switch:focus-visible { outline: 2px solid #485fc7; outline-offset: 2px; }
.hint { color: #7a7a7a; font-size: 0.75rem; }

.return-state { margin-top: 0.75rem; color: #946c00; }
.return-state.ready { color: #257942; }

.setup-label {
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-top: 1rem;
  padding: 1rem;
  border: 1px solid #dbdbdb;
  border-radius: 6px;
}
.setup-code { font-family: monospace; font-size: 1.25rem; font-weight: 600; }

.risky-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 0;
}
.risky-item + .risky-item { border-top: 1px solid #f5f5f5; }
.inline-form {
  margin-top: 0.75rem;
  padding: 1rem;
  border: 1px solid #ededed;
  border-radius: 6px;
}
.choice { display: flex; gap: 1.25rem; }
.risky-label { font-weight: 600; }
.risky-row .button { flex-shrink: 0; }

@media (max-width: 600px) {
  .facts { grid-template-columns: 1fr; gap: 0.1rem; }
  .facts dd { margin-bottom: 0.4rem; }
  .device-header, .risky-row { flex-direction: column; align-items: flex-start; }
}
</style>
